import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CustomDataGrid from "../../../common/js/components/CustomDataGrid";
import StatusChip from "../../../common/js/components/StatusChip";
import { pageSize } from "../utils/constants";
import { buildAddress, formatDate } from "../utils/operations";

const StyledLink = styled(Link)({
    textDecoration: "none",
    lineHeight: "1.2",
});

const StyledPropertyName = styled("span")({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: 600,
    wordWrap: "break-word",
    whiteSpace: "normal",
    lineHeight: "1.3",
});

const StyledPropertyCell = styled("div")({
    display: "flex",
    flexDirection: "column",
});

const StyledAddress = styled("div")({
    fontSize: "12px",
});

const ReviewPropertyGrid = ({
    properties,
    isLoading,
    getProperties,
    needsReviewPage,
    setNeedsReviewPage,
    setNeedsReviewSort,
    resetNeedsReviewTab,
}) => {
    const [sortModel, setSortModel] = useState([{ field: "auditLastModifiedTimestamp", sort: "desc" }]);
    const propertiesData = properties?.data_team_needs_review?.content ?? [];
    const totalCount = properties?.data_team_needs_review?.totalElements ?? 0;

    useEffect(() => {
        resetNeedsReviewTab();
        getProperties();
    }, []);

    const columns = [
        {
            field: "propertyName",
            headerName: "Property",
            flex: 2,
            minWidth: 250,
            renderCell: (params) => (
                <StyledPropertyCell>
                    <StyledLink to={`/propdetail/${params.row.id}`}>
                        <StyledPropertyName>{params.row?.propertyName ?? "-"}</StyledPropertyName>
                    </StyledLink>
                </StyledPropertyCell>
            ),
        },
        {
            field: "address",
            headerName: "Address",
            flex: 2,
            minWidth: 380,
            sortable: false,
            renderCell: (params) => (
                <StyledPropertyCell>
                    <StyledAddress>{buildAddress(params.row?.primaryAddress ?? {})}</StyledAddress>
                </StyledPropertyCell>
            ),
        },
        {
            field: "requester",
            headerName: "Requester",
            flex: 1,
            minWidth: 150,
            renderCell: (params) => params.row?.requester,
        },
        {
            field: "auditLastModifiedTimestamp",
            headerName: "Modified Date",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                return formatDate("MM-DD-YYYY")(params.row?.auditLastModifiedTimestamp);
            },
        },
        {
            field: "propertyType",
            headerName: "Property Type",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => params.row?.propertyType,
        },
        {
            field: "propertyStatus",
            headerName: "Status",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                return <StatusChip status={params.row?.propertyStatus ?? ""} />;
            },
        },
    ];

    const handlePageChange = (value) => {
        setNeedsReviewPage(value);
        getProperties();
    };

    const handleSortModelChange = (newSortModel) => {
        setNeedsReviewPage(1);
        setSortModel(newSortModel);
        const newSort =
            newSortModel[0]?.field && newSortModel[0]?.sort ? `${newSortModel[0].field},${newSortModel[0].sort}` : null;
        setNeedsReviewSort(newSort);
        getProperties();
    };

    return (
        <CustomDataGrid
            rows={propertiesData}
            columns={columns}
            totalCount={totalCount}
            page={needsReviewPage}
            rowHeight={35}
            pageSize={pageSize}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            onPageChange={(_, value) => handlePageChange(value)}
            loading={isLoading}
        />
    );
};

export default ReviewPropertyGrid;
